import { useMemo, useState } from 'react'
import { ChevronRight, Info, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import CheckPanel, { PackTag } from '@/frontend/features/Fde/dataHealth/CheckPanel'
import GapTimeline from '@/frontend/features/Fde/dataHealth/GapTimeline'
import {
  dotClass, layerLabel, metricHelp, metricLabel, statusClass, statusLabel, subjectLabel,
} from '@/frontend/features/Fde/dataHealth/labels'
import { checkWhere, describeCheck, tableSubject, type MeasureContext } from '@/frontend/features/Fde/dataHealth/measures'
import type { CheckGroup, HealthModel } from '@/frontend/features/Fde/dataHealth/model'
import { buildModel, groupChecks, layerRank, MAIN_LAYERS, sortChecks, TECHNICAL_LAYERS } from '@/frontend/features/Fde/dataHealth/model'
import { ColumnName, TableName } from '@/frontend/features/Fde/dataHealth/Names'
import type { CheckStatus, HealthCheck, HealthCopy, HealthRow, HealthRun, HealthSubject, HealthTable } from '@/frontend/features/Fde/dataHealth/types'
import { format, formatDate, formatNumber, formatPct, formatScalar } from '@/frontend/features/Fde/dataHealth/types'

type OpenCheck = (check: HealthCheck) => void

const TH = 'whitespace-nowrap px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'
const TD = 'whitespace-nowrap px-3 py-2 align-top'

function plural(copy: HealthCopy, key: string, count: number) {
  return format(copy[`${key}_${count === 1 ? 'one' : 'other'}`], { count: formatNumber(count) })
}

function Pill({ status, copy }: { status: CheckStatus; copy: HealthCopy }) {
  return (
    <span className={cn('inline-block whitespace-nowrap rounded-full border px-2 py-px text-[11px] font-semibold', statusClass[status])}>
      {statusLabel(status, copy)}
    </span>
  )
}

function Dot({ status, copy }: { status: CheckStatus | null; copy: HealthCopy }) {
  return (
    <span
      className={cn('inline-block size-2.5 shrink-0 rounded-full', status ? dotClass[status] : 'bg-muted-foreground/40')}
      role="img"
      aria-label={status ? statusLabel(status, copy) : copy.status_none}
    />
  )
}

// Metric name with a short plain explanation of what it measures and its limit.
function MetricName({ metric, copy, limit, className }: { metric: string; copy: HealthCopy; limit?: string | null; className?: string }) {
  const help = metricHelp(metric, copy)
  if (!help) return <span className={className}>{metricLabel(metric, copy)}</span>
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className={cn('inline-flex items-center gap-1 text-left underline decoration-dotted decoration-muted-foreground/60 underline-offset-2', className)}>
          {metricLabel(metric, copy)}
          <Info className="size-3 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={4} className="max-w-64 text-left">
        {help}{limit ? ` ${format(copy.metric_limit, { limit })}` : ''}
      </TooltipContent>
    </Tooltip>
  )
}

// Where the check looked: column names from the dictionary, partition or rule name.
function Where({ check, table, copy }: { check: HealthCheck; table?: HealthTable; copy: HealthCopy }) {
  const items = checkWhere(check, table, copy)
  if (!items.length) return null
  return (
    <>
      {items.map((item, index) => (
        <span key={`${item.kind}-${item.name}`}>
          {index > 0 && ', '}
          {item.kind === 'column' ? <ColumnName table={table} name={item.name} copy={copy} /> : <span className="break-words">{item.name}</span>}
        </span>
      ))}
    </>
  )
}

const GTH = 'py-1.5 pr-3 text-left align-bottom text-[11px] font-semibold uppercase tracking-wide text-muted-foreground last:pr-0'
const GTD = 'py-2 pr-3 align-top last:pr-0'

function onActivate(open: () => void) {
  return (event: React.KeyboardEvent) => {
    if (event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      open()
    }
  }
}

// One occurrence: table, column, the problem as measured and the limit. Click opens the side panel.
function OccurrenceRow({ check, ctx, onOpen }: { check: HealthCheck; ctx: MeasureContext; onOpen: OpenCheck }) {
  const { copy } = ctx
  const table = ctx.byId.get(check.table)
  const description = describeCheck(check, ctx)
  const limit = description.limit ?? copy.limit_none
  const hasWhere = checkWhere(check, table, copy).length > 0
  return (
    <tr
      tabIndex={0}
      aria-haspopup="dialog"
      aria-label={description.sentence}
      onClick={() => onOpen(check)}
      onKeyDown={onActivate(() => onOpen(check))}
      className="cursor-pointer border-t border-border/70 hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
    >
      <td className={GTD}>
        <span className="flex min-w-0 items-start gap-2">
          <span className={cn('mt-1.5 inline-block size-2 shrink-0 rounded-full', dotClass[check.status])} role="img" aria-label={statusLabel(check.status, copy)} />
          <span className="min-w-0 break-words">
            <TableName id={check.table} table={table} copy={copy} />
            {check.source === 'pack' && <PackTag copy={copy} />}
            {/* narrow: the column goes under the table */}
            {hasWhere && <span className="block text-xs text-muted-foreground @md:hidden"><Where check={check} table={table} copy={copy} /></span>}
          </span>
        </span>
      </td>
      <td className={cn(GTD, 'hidden break-words text-[13px] @md:table-cell')}>
        {hasWhere ? <Where check={check} table={table} copy={copy} /> : <span className="text-muted-foreground">-</span>}
      </td>
      <td className={cn(GTD, 'break-words font-medium tabular-nums')}>
        {description.problem}
        <span className="block text-[11px] font-normal text-muted-foreground @lg:hidden">{format(copy.limit_label, { limit })}</span>
      </td>
      <td className={cn(GTD, 'hidden break-words text-muted-foreground tabular-nums @lg:table-cell')}>{limit}</td>
    </tr>
  )
}

// A group: every occurrence of one measure, in a compact table.
function GroupTable({ group, ctx, onOpen }: { group: CheckGroup; ctx: MeasureContext; onOpen: OpenCheck }) {
  const { copy } = ctx
  const id = `grupo-${group.status}-${group.metric}`
  return (
    <section aria-labelledby={id} className="grid min-w-0 gap-1">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <h3 id={id} className="m-0 text-sm font-semibold"><MetricName metric={group.metric} copy={copy} /></h3>
        <span className="text-xs text-muted-foreground">{`${plural(copy, 'occurrences', group.checks.length)} ${plural(copy, 'tables_count', group.tables)}`}</span>
      </div>
      <table className="w-full table-fixed border-collapse text-[13px]">
        <thead>
          <tr>
            <th scope="col" className={cn(GTH, 'w-[36%]')}>{copy.col_table}</th>
            <th scope="col" className={cn(GTH, 'hidden w-[24%] @md:table-cell')}>{copy.col_column}</th>
            <th scope="col" className={cn(GTH, 'w-[24%]')}>{copy.col_problem}</th>
            <th scope="col" className={cn(GTH, 'hidden w-[16%] @lg:table-cell')}>{copy.col_limit}</th>
          </tr>
        </thead>
        <tbody>
          {group.checks.map((check) => <OccurrenceRow key={check.id} check={check} ctx={ctx} onOpen={onOpen} />)}
        </tbody>
      </table>
    </section>
  )
}

function AttentionBlock({ model, ctx, onOpen }: { model: HealthModel; ctx: MeasureContext; onOpen: OpenCheck }) {
  const { copy } = ctx
  const fails = groupChecks(model.checks, 'fail')
  const warns = groupChecks(model.checks, 'warn')
  return (
    <section aria-labelledby="atencao" className="grid grid-cols-[minmax(0,1fr)] gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="atencao" className="m-0 text-lg font-semibold">{copy.attention_title}</h2>
        <p className="m-0 flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {(['fail', 'warn', 'ok'] as CheckStatus[]).map((status) => (
            <span key={status} className="inline-flex items-center gap-1.5"><Dot status={status} copy={copy} /><span className="font-semibold tabular-nums">{formatNumber(model.counts[status])}</span> {statusLabel(status, copy)}</span>
          ))}
        </p>
      </div>
      {fails.length === 0
        ? <p className="m-0 rounded-xl border border-border bg-background px-4 py-3 text-sm text-muted-foreground">{copy.attention_none}</p>
        : (
          <div className="grid gap-6 rounded-xl border border-border bg-background px-4 py-3">
            {fails.map((group) => <GroupTable key={group.metric} group={group} ctx={ctx} onOpen={onOpen} />)}
          </div>
        )}
      {warns.length > 0 && (
        <details className="rounded-xl border border-border bg-background px-4 py-3">
          <summary className="cursor-pointer text-sm font-medium">
            {format(copy.warn_groups, { count: formatNumber(model.counts.warn), groups: warns.length })}
          </summary>
          <div className="mt-3 grid gap-6">
            {warns.map((group) => <GroupTable key={group.metric} group={group} ctx={ctx} onOpen={onOpen} />)}
          </div>
        </details>
      )}
    </section>
  )
}

function AccessNotice({ model, ctx }: { model: HealthModel; ctx: MeasureContext }) {
  const { copy } = ctx
  if (!model.unreadable.length) return null
  return (
    <aside className="grid gap-1 rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm" aria-label={copy.access_title}>
      <p className="m-0 flex items-start gap-2">
        <Lock className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span>{plural(copy, 'access_notice', model.unreadable.length)}</span>
      </p>
      <details className="pl-6 text-xs text-muted-foreground">
        <summary className="cursor-pointer">{copy.access_list}</summary>
        <p className="m-0 mt-1 break-words">{model.unreadable.map((id) => tableSubject(id, ctx)).join(', ')}</p>
      </details>
    </aside>
  )
}

function Examples({ rows, copy }: { rows?: HealthRow[]; copy: HealthCopy }) {
  if (!rows?.length) return null
  return (
    <details className="mt-1 text-xs">
      <summary className="cursor-pointer text-muted-foreground">{copy.examples} ({rows.length})</summary>
      <ul className="m-0 mt-1 grid list-none gap-0.5 p-0 font-mono text-[11px]">
        {rows.map((row, index) => (
          <li key={index} className="break-all">{Object.entries(row).map(([key, value]) => `${key}=${formatScalar(value)}`).join('  ')}</li>
        ))}
      </ul>
    </details>
  )
}

function CheckLine({ check, ctx, onOpen }: { check: HealthCheck; ctx: MeasureContext; onOpen: OpenCheck }) {
  const { copy } = ctx
  const table = ctx.byId.get(check.table)
  const description = describeCheck(check, ctx)
  const hasWhere = checkWhere(check, table, copy).length > 0
  return (
    <li className="border-t border-border first:border-t-0">
      <div
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-label={description.sentence}
        onClick={() => onOpen(check)}
        onKeyDown={onActivate(() => onOpen(check))}
        className="-mx-1 flex cursor-pointer flex-wrap items-start justify-between gap-x-3 gap-y-1 rounded px-1 py-2 hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
      >
        <span className="min-w-0 flex-1 basis-48">
          <span className="text-[13px]">{metricLabel(check.metric, copy)}</span>
          {check.source === 'pack' && <PackTag copy={copy} />}
          {hasWhere && <span className="block text-[11px] text-muted-foreground"><Where check={check} table={table} copy={copy} /></span>}
        </span>
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[13px] tabular-nums">
          <span className="font-semibold">{description.problem}</span>
          {description.limit && <span className="text-xs text-muted-foreground">{format(copy.limit_label, { limit: description.limit })}</span>}
          <Pill status={check.status} copy={copy} />
        </span>
      </div>
    </li>
  )
}

function ColumnsTable({ table, copy, staff }: { table: HealthTable; copy: HealthCopy; staff: boolean }) {
  if (!table.columns?.length) return null
  return (
    <details className="text-sm">
      <summary className="cursor-pointer text-xs text-muted-foreground">{format(copy.columns_toggle, { count: table.columns.length })}</summary>
      <div className="mt-2 overflow-x-auto rounded-lg border border-border" role="region" aria-label={format(copy.columns_of, { table: table.label?.trim() || table.table })} tabIndex={0}>
        <table className="w-full border-collapse text-xs tabular-nums">
          <thead className="bg-muted/30">
            <tr>
              <th className={TH}>{copy.col_column}</th>
              <th className={cn(TH, 'text-right')}>{copy.col_null_pct}</th>
              <th className={cn(TH, 'text-right')}>{copy.col_distinct}</th>
              <th className={cn(TH, 'text-right')}>{copy.col_min}</th>
              <th className={cn(TH, 'text-right')}>{copy.col_max}</th>
              <th className={cn(TH, 'text-right')}>{copy.col_outliers}</th>
            </tr>
          </thead>
          <tbody>
            {table.columns.map((column) => (
              <tr key={column.column} className="border-t border-border">
                <td className={cn(TD, 'whitespace-normal')}><ColumnName table={table} name={column.column} copy={copy} /></td>
                <td className={cn(TD, 'text-right')}>{formatPct(column.null_pct)}</td>
                <td className={cn(TD, 'text-right')}>{formatNumber(column.distinct)}</td>
                <td className={cn(TD, 'text-right')}>{formatScalar(column.min)}</td>
                <td className={cn(TD, 'text-right')}>{formatScalar(column.max)}</td>
                <td className={cn(TD, 'text-right')}>
                  {column.outliers ? formatNumber(column.outliers.count) : '-'}
                  {staff && <Examples rows={column.outliers?.examples} copy={copy} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}

function TableCard({ table, checks, ctx, staff, onOpen }: { table: HealthTable; checks: HealthCheck[]; ctx: MeasureContext; staff: boolean; onOpen: OpenCheck }) {
  const { copy } = ctx
  const sorted = sortChecks(checks)
  const open = sorted.filter((check) => check.status !== 'ok')
  const ok = sorted.filter((check) => check.status === 'ok')
  const key = table.key?.length ? table.key.join(', ') : null
  const meta = table.readable
    ? [
        format(copy.table_rows, { rows: formatNumber(table.rows) }),
        table.last_date ? format(copy.table_last_date, { date: formatDate(table.last_date) }) : null,
      ].filter(Boolean).join(' · ')
    : null
  return (
    <li className="grid gap-2 rounded-lg border border-border bg-background px-3 py-3">
      <div className="grid gap-0.5">
        <div className="text-sm">
          <TableName id={table.id} table={table} copy={copy} />
        </div>
        {table.readable ? (
          <p className="m-0 text-xs text-muted-foreground">
            {meta}
            {key && <> · {copy.key_label}: <span className="font-mono">{key}</span>{table.key_source === 'inferred' && <span className="ml-1 rounded border border-border px-1 text-[10px]">{copy.key_inferred}</span>}</>}
          </p>
        ) : (
          <p className="m-0 inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="size-3" aria-hidden />{copy.no_access}</p>
        )}
      </div>
      {table.readable && (open.length > 0
        ? <ul className="m-0 list-none p-0">{open.map((check) => <CheckLine key={check.id} check={check} ctx={ctx} onOpen={onOpen} />)}</ul>
        : !ok.length && <p className="m-0 text-xs text-muted-foreground">{copy.no_checks_table}</p>)}
      {ok.length > 0 && (
        <details>
          <summary className="cursor-pointer text-xs text-muted-foreground">{format(copy.checks_ok_toggle, { count: ok.length })}</summary>
          <ul className="m-0 mt-1 list-none p-0">{ok.map((check) => <CheckLine key={check.id} check={check} ctx={ctx} onOpen={onOpen} />)}</ul>
        </details>
      )}
      {staff && table.duplicate_keys?.examples?.length ? <Examples rows={table.duplicate_keys.examples} copy={copy} /> : null}
      {table.readable && <ColumnsTable table={table} copy={copy} staff={staff} />}
    </li>
  )
}

function LayerBlock({ layer, tables, model, ctx, staff, onOpen }: { layer: string; tables: HealthTable[]; model: HealthModel; ctx: MeasureContext; staff: boolean; onOpen: OpenCheck }) {
  const { copy } = ctx
  return (
    <div className="grid gap-2">
      <h4 className="m-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{layerLabel(layer, copy)}</h4>
      <ul className="m-0 grid list-none gap-2 p-0">
        {tables.map((table) => <TableCard key={table.id} table={table} checks={model.checksByTable.get(table.id) ?? []} ctx={ctx} staff={staff} onOpen={onOpen} />)}
      </ul>
    </div>
  )
}

function SubjectDetails({ subject, model, ctx, staff, onOpen }: { subject: HealthSubject; model: HealthModel; ctx: MeasureContext; staff: boolean; onOpen: OpenCheck }) {
  const { copy } = ctx
  const tables = subject.tables.map((id) => model.byId.get(id)).filter((table): table is HealthTable => !!table)
  const byLayer = (layers: string[]) => layers
    .map((layer) => [layer, tables.filter((table) => table.layer === layer)] as const)
    .filter(([, items]) => items.length)
  const main = byLayer(MAIN_LAYERS)
  const technical = byLayer(TECHNICAL_LAYERS).sort((a, b) => layerRank(a[0]) - layerRank(b[0]))
  const technicalChecks = technical.flatMap(([, items]) => items.flatMap((table) => model.checksByTable.get(table.id) ?? []))
  const techFail = technicalChecks.filter((check) => check.status === 'fail').length
  const techWarn = technicalChecks.filter((check) => check.status === 'warn').length
  return (
    <div className="grid gap-4 border-t border-border bg-muted/20 px-3 py-4 @3xl:px-4">
      {main.length === 0 && <p className="m-0 text-xs text-muted-foreground">{copy.no_main_layers}</p>}
      {main.map(([layer, items]) => <LayerBlock key={layer} layer={layer} tables={items} model={model} ctx={ctx} staff={staff} onOpen={onOpen} />)}
      {technical.length > 0 && (
        <details className="grid gap-3">
          <summary className="cursor-pointer text-sm font-medium">
            {copy.technical_layers}
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {[technical.map(([layer]) => layerLabel(layer, copy)).join(', '),
                techFail ? `${formatNumber(techFail)} ${statusLabel('fail', copy)}` : null,
                techWarn ? `${formatNumber(techWarn)} ${statusLabel('warn', copy)}` : null].filter(Boolean).join(' · ')}
            </span>
          </summary>
          <div className="mt-3 grid gap-4">
            {technical.map(([layer, items]) => <LayerBlock key={layer} layer={layer} tables={items} model={model} ctx={ctx} staff={staff} onOpen={onOpen} />)}
          </div>
        </details>
      )}
    </div>
  )
}

const ROW_GRID = 'grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 @3xl:grid-cols-[auto_minmax(0,0.9fr)_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_auto]'

function SubjectRow({ subject, model, ctx, staff, onOpen }: { subject: HealthSubject; model: HealthModel; ctx: MeasureContext; staff: boolean; onOpen: OpenCheck }) {
  const { copy } = ctx
  const [open, setOpen] = useState(false)
  const layers = [...subject.layers].sort((a, b) => layerRank(a) - layerRank(b))
  const lastDate = formatDate(subject.last_date) || '-'
  const days = subject.days_since_last == null ? '-' : formatNumber(subject.days_since_last)
  return (
    <li className="border-t border-border first:border-t-0">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className={cn(ROW_GRID, 'w-full px-3 py-3 text-left text-sm hover:bg-muted/30 @3xl:px-4')}>
        <Dot status={subject.status} copy={copy} />
        <span className="min-w-0">
          <span className="block font-semibold">{subjectLabel(subject.id, copy)}</span>
          {/* phone: the numbers go under the name */}
          <span className="block text-xs text-muted-foreground @3xl:hidden">
            {[layers.map((layer) => layerLabel(layer, copy)).join(', '),
              format(copy.table_rows, { rows: formatNumber(subject.rows) }),
              format(copy.subject_last_date_short, { date: lastDate, days })].join(' · ')}
          </span>
        </span>
        <span className="hidden flex-wrap gap-1 @3xl:flex">
          {layers.map((layer) => (
            <span key={layer} className={cn('rounded border px-1.5 py-px text-[11px]', MAIN_LAYERS.includes(layer) ? 'border-border bg-background' : 'border-dashed border-border text-muted-foreground')}>
              {layerLabel(layer, copy)}
            </span>
          ))}
        </span>
        <span className="hidden text-right tabular-nums @3xl:block">{formatNumber(subject.rows)}</span>
        <span className="hidden tabular-nums @3xl:block">{lastDate}</span>
        <span className="hidden text-right tabular-nums @3xl:block">{days}</span>
        <ChevronRight className={cn('size-4 text-muted-foreground transition-transform', open && 'rotate-90')} aria-hidden />
      </button>
      {open && <SubjectDetails subject={subject} model={model} ctx={ctx} staff={staff} onOpen={onOpen} />}
    </li>
  )
}

type StatusFilter = 'all' | CheckStatus

function Filters({ status, setStatus, subject, setSubject, subjects, copy }: {
  status: StatusFilter; setStatus: (value: StatusFilter) => void; subject: string; setSubject: (value: string) => void
  subjects: HealthSubject[]; copy: HealthCopy
}) {
  const options: StatusFilter[] = ['all', 'fail', 'warn', 'ok']
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
      <div role="group" aria-label={copy.filter_status} className="inline-flex flex-wrap gap-1">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={status === option}
            onClick={() => setStatus(option)}
            className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1', status === option ? 'border-foreground bg-foreground text-background' : 'border-border bg-background hover:bg-muted/40')}
          >
            {option !== 'all' && <Dot status={option} copy={copy} />}
            {copy[`filter_status_${option}`]}
          </button>
        ))}
      </div>
      <label className="inline-flex items-center gap-2 text-muted-foreground">
        {copy.filter_subject}
        <select value={subject} onChange={(event) => setSubject(event.target.value)} className="h-8 max-w-48 rounded-md border border-border bg-background px-2 text-xs text-foreground">
          <option value="">{copy.filter_subject_all}</option>
          {subjects.map((item) => <option key={item.id} value={item.id}>{subjectLabel(item.id, copy)}</option>)}
        </select>
      </label>
    </div>
  )
}

function SubjectsSection({ model, ctx, staff, status, subject, onOpen }: { model: HealthModel; ctx: MeasureContext; staff: boolean; status: StatusFilter; subject: string; onOpen: OpenCheck }) {
  const { copy } = ctx
  const visible = model.subjects.filter((item) => (status === 'all' || item.status === status) && (!subject || item.id === subject))
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <div className={cn(ROW_GRID, 'hidden border-b border-border bg-muted/30 px-3 @3xl:grid @3xl:px-4')} aria-hidden>
        <span className="size-2.5" />
        <span className={cn(TH, 'px-0')}>{copy.col_subject}</span>
        <span className={cn(TH, 'px-0')}>{copy.col_layers}</span>
        <span className={cn(TH, 'px-0 text-right')}>{copy.col_final_rows}</span>
        <span className={cn(TH, 'px-0')}>{copy.col_last_date}</span>
        <span className={cn(TH, 'px-0 text-right')}>{copy.col_days_without}</span>
        <span className="size-4" />
      </div>
      {visible.length === 0
        ? <p className="m-0 px-4 py-3 text-sm text-muted-foreground">{copy.subjects_empty}</p>
        : <ul className="m-0 list-none p-0">{visible.map((item) => <SubjectRow key={item.id} subject={item} model={model} ctx={ctx} staff={staff} onOpen={onOpen} />)}</ul>}
    </div>
  )
}

export default function DataHealthReport({ run, copy, staff }: { run: HealthRun; copy: HealthCopy; staff: boolean }) {
  const model = useMemo(() => buildModel(run), [run])
  const ctx = useMemo<MeasureContext>(() => ({ byId: model.byId, relationships: model.relationships, ambiguous: model.ambiguous, copy }), [model, copy])
  const [selected, setSelected] = useState<HealthCheck | null>(null)
  const [status, setStatus] = useState<StatusFilter>('all')
  const [subject, setSubject] = useState('')
  const [technical, setTechnical] = useState(false)
  const timelineTables = (run.tables ?? []).filter((table) =>
    (technical || MAIN_LAYERS.includes(table.layer))
    && (!subject || (model.subjects.find((item) => item.id === subject)?.tables ?? []).includes(table.id)))

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-8">
      <AttentionBlock model={model} ctx={ctx} onOpen={setSelected} />
      <AccessNotice model={model} ctx={ctx} />

      <section aria-labelledby="assuntos" className="grid grid-cols-[minmax(0,1fr)] gap-3">
        <h2 id="assuntos" className="m-0 text-lg font-semibold">{copy.subjects_title}</h2>
        <Filters status={status} setStatus={setStatus} subject={subject} setSubject={setSubject} subjects={model.subjects} copy={copy} />
        <SubjectsSection model={model} ctx={ctx} staff={staff} status={status} subject={subject} onOpen={setSelected} />
      </section>

      {(run.tables ?? []).length > 0 && (
        <section aria-labelledby="periodo" className="grid grid-cols-[minmax(0,1fr)] gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 id="periodo" className="m-0 text-lg font-semibold">{copy.timeline}</h2>
            <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={technical} onChange={(event) => setTechnical(event.target.checked)} className="size-3.5" />
              {copy.timeline_technical}
            </label>
          </div>
          {timelineTables.some((table) => table.first_date)
            ? <GapTimeline tables={timelineTables} copy={copy} />
            : <p className="m-0 text-sm text-muted-foreground">{copy.timeline_empty}</p>}
        </section>
      )}

      <CheckPanel check={selected} ctx={ctx} staff={staff} onClose={() => setSelected(null)} />
    </div>
  )
}
